import { useEffect, useState } from 'react';
import { Box, Typography, Breadcrumbs, Link, Button, Divider } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useQuery } from 'react-query';
import { apiGetAllBrands } from '../../../api/apiBrands';

interface ImageMatch {
  image_data?: string;
  file_name?: string;
  brand_name?: string;
}

// 🔥 UPDATED: Interface to match what's being passed
interface ProductSectionProps {
  productListData: any[]; // This is the main product array
  productData?: any; // This might be the wrapper object
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  itemGroup: string | undefined;
  application: string | undefined;
  collection: string | undefined;
  design: string | undefined;
  colour?: string | undefined;
  currentSelectedItem?: any;
  setCurrentSelectedItem: (item: any) => void;
  setSelectedImagePreviewIndex: (index: number) => void;
  selectedImagePreviewIndex: number;
  // 🔥 ADDED: Props that might be missing but are used in trade repo
  setWidth?: (width: string) => void;
  setLengthCM?: (length: string) => void;
  setDiameter?: (diameter: string) => void;
  diameter?: string;
  width?: string;
  lengthCM?: string;
  activeImageStep?: number;
  setActiveImageStep?: (step: number) => void;
  isInitialLoad?: boolean;
  setIsInitialLoad?: (value: boolean) => void;
  userHasInteracted?: boolean;
  setUserHasInteracted?: (value: boolean) => void;
}

const ProductSection = ({
                          productListData, // 🔥 USING: This should be the main data array
                          productData,
                          currentIndex,
                          setCurrentIndex,
                          itemGroup,
                          application,
                          collection,
                          design,
                          colour,
                          currentSelectedItem,
                          setCurrentSelectedItem,
                          setSelectedImagePreviewIndex,
                          selectedImagePreviewIndex,
                          setWidth,
                          setLengthCM,
                          setDiameter,
                          diameter,
                          width,
                          lengthCM,
                          activeImageStep,
                          setActiveImageStep,
                          isInitialLoad,
                          setIsInitialLoad,
                          userHasInteracted,
                          setUserHasInteracted,
                        }: ProductSectionProps) => {
  const base = window.location.origin;

  // Helper function to get property from either nested or flat structure
  const getProperty = (item: any, prop: string): any => {
    if (!item) return undefined;

    // First try direct access (flattened structure)
    if (item[prop] !== undefined) {
      return item[prop];
    }
    // Then try StockMaster (nested structure)
    if (item?.StockMaster && item.StockMaster[prop] !== undefined) {
      return item.StockMaster[prop];
    }
    // Then try Product (nested structure)
    if (item?.Product && item.Product[prop] !== undefined) {
      return item.Product[prop];
    }
    return undefined;
  };

  // 🔥 FIXED: Use productListData instead of productData for current product
  const currentProduct = productListData && productListData[currentIndex] ? productListData[currentIndex] : null;

  // 🔥 FIXED: Get current application from the actual product data
  const currentApplication = currentProduct ?
    (getProperty(currentProduct, 'application') || application) :
    application;

  // 🔥 DEBUG: Log what we're working with
  console.log('=== ProductSection Debug ===');
  console.log('productListData:', productListData);
  console.log('currentIndex:', currentIndex);
  console.log('currentProduct:', currentProduct);
  console.log('Current application:', currentApplication);

  if (currentProduct) {
    console.log('Group desc:', getProperty(currentProduct, 'group_desc'));
    console.log('Long description:', getProperty(currentProduct, 'long_description'));
    console.log('Brand:', getProperty(currentProduct, 'brand'));
    console.log('Design:', getProperty(currentProduct, 'design'));
    console.log('Colour:', getProperty(currentProduct, 'colour'));
  }

  const [imageMatch, setImageMatch] = useState<ImageMatch>({});
  const [showFullDescription, setShowFullDescription] = useState(false);

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  // 🔥 UPDATED: Handle both data structures in setCurrentSelectedItem
  useEffect(() => {
    if (!productListData || !currentProduct) {
      console.log('No product data available for setCurrentSelectedItem');
      return;
    }

    console.log('Setting current selected item with:', currentProduct);

    setCurrentSelectedItem({
      image: getProperty(currentProduct, 'primaryImageUrl'),
      name: getProperty(currentProduct, 'colour'),
      quantity: 1,
      stock: getProperty(currentProduct, 'on_hand'),
      sku: getProperty(currentProduct, 'sku'),
      description: getProperty(currentProduct, 'disambiguating_description'),
      group: getProperty(currentProduct, 'group_desc'),
      cut_price: getProperty(currentProduct, 'cut_price_inc_vat'),
      roll_price: getProperty(currentProduct, 'roll_price_inc_vat'),
      retail_price: getProperty(currentProduct, 'price_retail_inc_vat'),
      id: getProperty(currentProduct, 'id'),
      availableColourImages:
        getProperty(currentProduct, 'additionalImages') === null
          ? [
            {
              image_data: getProperty(currentProduct, 'primaryImageUrl'),
            },
          ]
          : getProperty(currentProduct, 'additionalImages'),
    });
  }, [currentIndex, productListData, setCurrentSelectedItem, currentProduct]);

  // 🔥 UPDATED: Brand query with better error handling
  useQuery(
    ['brands', getProperty(currentProduct, 'brand')],
    async () => {
      const response = await apiGetAllBrands();
      if (response) {
        const foundImageMatch = response.find(
          (element: any) =>
            element.brand_name === getProperty(currentProduct, 'brand')
        );
        if (foundImageMatch) {
          setImageMatch(foundImageMatch);
        }
      }
      return response;
    },
    {
      initialData: [],
      enabled: !!currentProduct && !!getProperty(currentProduct, 'brand'),
    }
  );

  // 🔥 UPDATED: Handle delivery date
  const timeStamp = currentProduct ? Number(getProperty(currentProduct, 'po_delivery_date')) : 0;
  const date = new Date(timeStamp);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  // 🔥 EARLY RETURN: If no product data, show loading or empty state
  if (!productListData || productListData.length === 0 || !currentProduct) {
    console.log('No product data available, showing empty state');
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading product information...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        pr: { xs: '0', lg: '50px' },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          padding: {
            xs: '0 0',
            lg: '0 2rem',
          },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Breadcrumbs
          sx={{
            display: {
              xs: 'none',
              sm: 'block',
            },
          }}
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          {/* Level 1: Item Group */}
          <Link
            underline="hover"
            color="inherit"
            href={`${base}/item_group/${itemGroup}`}
          >
            {getProperty(productListData[0], 'group_desc') || 'Group'}
          </Link>

          {/* Level 2: Collection */}
          <Link
            underline="hover"
            color="inherit"
            href={`${base}/item_group/${itemGroup}/collection/${encodeURIComponent(collection || '')}`}
          >
            {collection || 'Collection'}
          </Link>

          {/* Level 3: Design (current page - no link) */}
          <Typography color="text.primary">{design || 'Design'}</Typography>
        </Breadcrumbs>

        {/* 🔥 FIXED: Product title and details */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 'auto',
        }}>
          <Box>
            <Typography
              mt={1}
              variant="h5"
              fontSize={'40px'}
              fontWeight={'500'}
              lineHeight={'40px'}
            >
              {getProperty(productListData[0], 'design') || design || 'Design Name'}
            </Typography>
            <Typography fontWeight="600" lineHeight={'24px'} fontSize={'16px'}>
              {getProperty(currentProduct, 'colour') || 'Colour'}
            </Typography>
          </Box>
          <Box sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
          }}>
            {imageMatch?.image_data && (
              <img
                src={imageMatch.image_data}
                alt={imageMatch.brand_name || 'Brand'}
                style={{
                  objectFit: 'contain',
                  width: '30%',
                }}
              />
            )}
          </Box>
        </Box>

        {/* 🔥 FIXED: Long description handling */}
        {getProperty(currentProduct, 'long_description') &&
        getProperty(currentProduct, 'long_description').length > 0 ? (
          <Typography
            mt={2}
            fontSize={'16px'}
            fontWeight={'400'}
            lineHeight={'25px'}
          >
            {getProperty(currentProduct, 'long_description').length > 500
              ? showFullDescription
                ? getProperty(currentProduct, 'long_description')
                : getProperty(currentProduct, 'long_description').substring(0, 500) + '...'
              : getProperty(currentProduct, 'long_description')}
            {getProperty(currentProduct, 'long_description').length > 500 && (
              <Button
                size="small"
                onClick={toggleDescription}
                sx={{
                  textTransform: 'none',
                  cursor: 'pointer',
                  p: '0',
                  height: '0',
                  minWidth: '0',
                  mt: 2,
                }}
              >
                {showFullDescription ? 'Less' : 'More'}
              </Button>
            )}
          </Typography>
        ) : null}

        {/* 🔥 FIXED: Thumbnail navigation */}
        <Box
          sx={{
            mt: '20px',
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
            overflowX: productListData?.length >= 6 ? 'auto' : 'hidden',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px',
              border: '2px solid transparent',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: '#888 transparent',
          }}
        >
          {productListData &&
            productListData.length > 0 &&
            productListData.map((product: any, index: number) => {
              const productImageUrl = getProperty(product, 'primaryImageUrl');
              const productColour = getProperty(product, 'colour');

              return (
                <Box
                  key={index}
                  sx={{
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                  onClick={() => {
                    console.log('Thumbnail clicked for index:', index);
                    setSelectedImagePreviewIndex(index);
                    setCurrentIndex(index);

                    // 🔥 ADDED: Handle user interaction tracking
                    if (setUserHasInteracted) {
                      setUserHasInteracted(true);
                    }
                    if (isInitialLoad && setIsInitialLoad) {
                      setIsInitialLoad(false);
                    }
                  }}
                >
                  {productImageUrl ? (
                    <img
                      style={{
                        height: '50px',
                        width: '50px',
                        padding: '5px',
                        marginRight: '0',
                        border:
                          selectedImagePreviewIndex === index
                            ? '1px solid black'
                            : 'none',
                        objectFit: 'cover',
                      }}
                      src={productImageUrl}
                      alt={`${productColour || 'Product'}`}
                      onError={(e) => {
                        console.log('Image failed to load:', productImageUrl);
                        // You could set a default image here
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: '50px',
                        width: '50px',
                        padding: '5px',
                        marginRight: '0',
                        border:
                          selectedImagePreviewIndex === index
                            ? '1px solid black'
                            : '1px solid #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      <Typography fontSize="8px">No Image</Typography>
                    </Box>
                  )}
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: '600',
                      textAlign: 'center',
                      mt: '4px',
                      color: selectedImagePreviewIndex === index ? '#000' : '#666',
                    }}
                  >
                    {productColour || 'N/A'}
                  </Typography>
                </Box>
              );
            })}
        </Box>

        {/* Fabric / Wallpaper ordering note */}
        {(getProperty(currentProduct, 'group_desc') === 'Fabric' ||
          getProperty(currentProduct, 'group_desc') === 'Wallpaper') && (
          <Box sx={{ mt: '20px' }}>
            <Divider sx={{ mb: '20px' }} />
            <Typography
              fontSize={'16px'}
              fontWeight={'700'}
              lineHeight={'25px'}
            >
              Available to order via decorators and specialist curtaining or upholstery workshops.
            </Typography>
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default ProductSection;